import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import {
  ApiError,
  LAB_REPORT_MAX_BYTES,
  SELF_REPORTABLE_ANALYTES,
  SELF_REPORT_UNITS,
  type Market,
} from "@ferrivia/api-contract";
import { useApi, useTracker } from "@ferrivia/auth-web";
import { useMarket, useTranslation } from "@ferrivia/i18n";
import {
  Button,
  Field,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Sheet,
  SheetContent,
  SheetTitle,
  Text,
} from "@ferrivia/ui";
import { useAppContext } from "../../app/useAppContext";
import { useDateInput } from "../DateInput";
import { useDismissOnBack } from "../../hooks/useDismissOnBack";
import { useKeyboardScrollOffset } from "../../hooks/useKeyboardScrollOffset";
import { focusWithoutPan } from "../../onboarding/focusWithoutPan";
import s from "./UploadSheet.module.css";

type Analyte = (typeof SELF_REPORTABLE_ANALYTES)[number];

const ANALYTE_PLACEHOLDER_EXAMPLE: Record<Market, Record<Analyte, string>> = {
  fi: { ferritin: "45", crp: "0.3", hemoglobin: "130" },
  us: { ferritin: "45", crp: "0.3", hemoglobin: "13.0" },
};

/** Native `<input type="number">` only accepts "." as a decimal separator
    regardless of locale, rejecting the comma Finnish users type — so this
    field is `type="text"` and normalizes the separator itself. */
function normalizeDecimalInput(raw: string): string {
  return raw.replace(",", ".").replace(/[^0-9.]/g, "");
}

interface AnalyteRow {
  key: string;
  analyte: Analyte;
  value: string;
  /** Undefined until the client picks one; the analyte's first unit stands. */
  unit?: string;
  /** Fixed analyte, not removable: the market's pilot needs this value. */
  required?: boolean;
}

const REQUIRED_ANALYTES: Record<Market, readonly Analyte[]> = {
  fi: [],
  us: ["ferritin", "hemoglobin"],
};

function initialRows(market: Market): AnalyteRow[] {
  const required = REQUIRED_ANALYTES[market];
  if (required.length === 0) return [{ key: crypto.randomUUID(), analyte: "ferritin", value: "" }];
  return required.map((analyte) => ({ key: crypto.randomUUID(), analyte, value: "", required: true }));
}

function firstUnusedAnalyte(rows: AnalyteRow[]): Analyte | null {
  const used = new Set(rows.map((r) => r.analyte));
  return SELF_REPORTABLE_ANALYTES.find((a) => !used.has(a)) ?? null;
}

type ReportFailure = "not_pdf" | "too_large" | "network" | "unknown";

function reportFailureFor(err: unknown): ReportFailure {
  if (err instanceof ApiError) {
    if (err.status === 415) return "not_pdf";
    if (err.status === 413) return "too_large";
    return "unknown";
  }
  return "network";
}

export interface UploadSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

/** Mounted inside SheetContent so the keyboard handling lives only while the
    sheet is open — mounting it with the screen would pin the app height for
    every other surface too. Reports what it measures upwards, because the
    sheet that has to respond to it is its own parent. */
function SheetKeyboardHandler({
  formRef,
  onKeyboardHeight,
}: {
  formRef: RefObject<HTMLDivElement | null>;
  onKeyboardHeight: (height: number) => void;
}) {
  const hidden = useKeyboardScrollOffset(formRef);

  useEffect(() => {
    onKeyboardHeight(hidden);
  }, [hidden, onKeyboardHeight]);

  return null;
}

export function UploadSheet({ open, onOpenChange, onSaved }: UploadSheetProps) {
  const api = useApi();
  const { t } = useTranslation("client");
  const tracker = useTracker();
  const market = useMarket();
  const { rootElement } = useAppContext();
  const [rows, setRows] = useState<AnalyteRow[]>(() => initialRows(market));
  const [date, setDate] = useState("");
  const dateInput = useDateInput(date, setDate);
  const [sourceLab, setSourceLab] = useState("");
  // Generated once for this mount of the sheet, not per save attempt: a retry
  // after a lost response must reuse it so the server's idempotency key
  // dedupes the retry rather than recording the batch twice. See
  // QuickCheckin.tsx's identical note for clientCheckinId.
  const [clientResultId, setClientResultId] = useState(() => crypto.randomUUID());
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reportFile, setReportFile] = useState<File | null>(null);
  const [reportFailure, setReportFailure] = useState<ReportFailure | null>(null);
  // Kept across a failed save so a retry does not upload the PDF a second time.
  const [uploadedReportId, setUploadedReportId] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  useDismissOnBack(open, () => onOpenChange(false), "uploadResults");

  /* On the way in, not on the way out: the sheet is now animated off screen
     rather than removed, so clearing the fields as it closes would show them
     emptying out for the length of the slide. A layout effect, so the reset
     is committed before the reopened sheet is ever painted. */
  useLayoutEffect(() => {
    if (!open) return;
    setRows(initialRows(market));
    setDate("");
    setSourceLab("");
    setClientResultId(crypto.randomUUID());
    setKeyboardHeight(0);
    setSaving(false);
    setFailed(false);
    setReportFile(null);
    setReportFailure(null);
    setUploadedReportId(null);
  }, [open, market]);

  const analyteLabels: Record<Analyte, string> = {
    ferritin: t("results.upload.analytes.ferritin", "Ferritiini"),
    crp: t("results.upload.analytes.crp", "CRP"),
    hemoglobin: t("results.upload.analytes.hemoglobin", "Hemoglobiini"),
  };

  const nextAnalyte = firstUnusedAnalyte(rows);
  const canAddRow = rows.length < SELF_REPORTABLE_ANALYTES.length;
  const usReport = market === "us" && reportFile !== null;
  const enteredRows = rows.filter((r) => r.value !== "");
  const canSave = date !== "" && (usReport || enteredRows.length === rows.length) && !saving;

  function handleReportChosen(file: File | null) {
    setReportFailure(null);
    setUploadedReportId(null);
    if (file && file.size > LAB_REPORT_MAX_BYTES) {
      tracker.track("results.report_upload_failed", { reason: "too_large" });
      setReportFailure("too_large");
      setReportFile(null);
      return;
    }
    if (file && file.type !== "application/pdf") {
      tracker.track("results.report_upload_failed", { reason: "not_pdf" });
      setReportFailure("not_pdf");
      setReportFile(null);
      return;
    }
    setReportFile(file);
  }

  function updateRow(key: string, patch: Partial<AnalyteRow>) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function addRow() {
    if (!nextAnalyte) return;
    setRows((prev) => [...prev, { key: crypto.randomUUID(), analyte: nextAnalyte, value: "" }]);
  }

  function removeRow(key: string) {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key || r.required) : prev));
  }

  async function handleSave() {
    setSaving(true);
    setFailed(false);
    let reportFileId = uploadedReportId;
    if (market === "us" && reportFile && reportFileId === null) {
      tracker.track("results.report_upload_started", {});
      try {
        reportFileId = (await api.labs.uploadReportFile(reportFile, date)).id;
        setUploadedReportId(reportFileId);
      } catch (err) {
        const reason = reportFailureFor(err);
        tracker.track("results.report_upload_failed", { reason });
        setReportFailure(reason);
        setSaving(false);
        return;
      }
    }
    if (enteredRows.length === 0) {
      tracker.track("results.upload_saved", {});
      onOpenChange(false);
      onSaved();
      setSaving(false);
      return;
    }
    try {
      await api.labs.saveSelfReported({
        clientResultId,
        collectedOn: date,
        sourceLab: sourceLab || null,
        ...(reportFileId ? { reportFileId } : {}),
        analytes: enteredRows.map((r) => ({
          analyteCode: r.analyte,
          value: Number(r.value),
          ...(market === "us" ? { unit: r.unit ?? SELF_REPORT_UNITS.us[r.analyte][0] } : {}),
        })),
      });
      tracker.track("results.upload_saved", {});
      onOpenChange(false);
      onSaved();
    } catch (err) {
      tracker.track("results.save_failed", { reason: err instanceof ApiError ? err.code : "network" });
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        container={rootElement}
        keyboardHeight={keyboardHeight}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetKeyboardHandler formRef={formRef} onKeyboardHeight={setKeyboardHeight} />
        <SheetTitle className={s.title}>
          {t("results.upload.title", "Lisää omat tulokset")}
        </SheetTitle>
        <div ref={formRef} className={s.form}>
          {rows.map((row, index) => {
            const units: readonly string[] = SELF_REPORT_UNITS[market][row.analyte];
            const unit = row.unit ?? units[0]!;
            const valueLabel = `${analyteLabels[row.analyte]} (${unit})`;
            const usedByOtherRows = new Set(rows.filter((r) => r.key !== row.key).map((r) => r.analyte));
            return (
              <div key={row.key} className={s.analyteRow}>
                {!row.required && (
                  <div className={s.field}>
                    <label className={s.label}>
                      {t("results.upload.analyteLabel", "Tutkimus")}
                    </label>
                    <Select
                      value={row.analyte}
                      onValueChange={(v) => updateRow(row.key, { analyte: v as Analyte, unit: undefined })}
                    >
                      <SelectTrigger />
                      <SelectContent>
                        {SELF_REPORTABLE_ANALYTES.filter((a) => a === row.analyte || !usedByOtherRows.has(a)).map(
                          (a) => (
                            <SelectItem key={a} value={a}>
                              {analyteLabels[a]}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {units.length > 1 && (
                  <div className={s.field}>
                    <label className={s.label}>{t("results.upload.unitLabel", "Yksikkö")}</label>
                    <Select value={unit} onValueChange={(v) => updateRow(row.key, { unit: v })}>
                      <SelectTrigger />
                      <SelectContent>
                        {units.map((u) => (
                          <SelectItem key={u} value={u}>
                            {u}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Field
                  label={valueLabel}
                  type="text"
                  inputMode="decimal"
                  placeholder={t("results.upload.valuePlaceholder", "esim. {{example}}", {
                    example: ANALYTE_PLACEHOLDER_EXAMPLE[market][row.analyte],
                  })}
                  value={row.value}
                  onChange={(e) => updateRow(row.key, { value: normalizeDecimalInput(e.target.value) })}
                  onMouseDown={focusWithoutPan}
                />
                {rows.length > 1 && !row.required && (
                  <Button variant="ghost" className={s.removeRow} onClick={() => removeRow(row.key)}>
                    {t("results.upload.removeRow", "Poista")}
                  </Button>
                )}
                {index < rows.length - 1 && <div className={s.rowDivider} />}
              </div>
            );
          })}
          {canAddRow && nextAnalyte && (
            <Button variant="ghost" onClick={addRow}>
              {REQUIRED_ANALYTES[market].length > 0
                ? t("results.upload.addOptionalRow", "+ Lisää {{analyte}} (valinnainen)", {
                    analyte: analyteLabels[nextAnalyte],
                  })
                : t("results.upload.addRow", "+ Lisää toinen arvo")}
            </Button>
          )}
          <Field
            label={t("results.upload.dateLabel", "Näytteenottopäivä")}
            {...dateInput}
            onMouseDown={focusWithoutPan}
          />
          <Field
            label={t("results.upload.sourceLabLabel", "Laboratorio")}
            type="text"
            placeholder={t("results.upload.sourceLabPlaceholder", "esim. Oma lääkäriasema")}
            value={sourceLab}
            onChange={(e) => setSourceLab(e.target.value)}
            onMouseDown={focusWithoutPan}
          />
          {market === "us" && (
            <div className={s.field}>
              <label className={s.label} htmlFor="lab-report-file">
                {t("results.upload.reportLabel", "Liitä laboratorioraportti (PDF)")}
              </label>
              <input
                id="lab-report-file"
                type="file"
                accept="application/pdf"
                onChange={(e) => handleReportChosen(e.target.files?.[0] ?? null)}
              />
              {reportFailure && (
                <Text as="p" size="sm" className={s.error}>
                  {reportFailure === "not_pdf"
                    ? t("results.upload.reportNotPdf", "Valitse PDF-tiedosto.")
                    : reportFailure === "too_large"
                      ? t("results.upload.reportTooLarge", "PDF on liian suuri. Enimmäiskoko on 10 Mt.")
                      : t("results.upload.reportFailed", "Raportin lähetys epäonnistui. Yritä uudestaan.")}
                </Text>
              )}
            </div>
          )}
          {failed && (
            <Text as="p" size="sm" className={s.error}>
              {t("results.upload.saveFailed", "Tallennus epäonnistui. Yritä uudestaan.")}
            </Text>
          )}
          <Button className={s.submit} disabled={!canSave} onClick={handleSave}>
            {t("results.upload.save", "Tallenna")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
